import { useEffect } from "react";

/**
 * Custom hook to register global keyboard shortcuts.
 * @param keys Array of lowercase keys (e.g. ["ctrl", "s"] or ["esc"])
 * @param callback Callback function when combination matches
 * @param disabled Disable shortcut listener
 */
export function useKeyboardShortcut(
  keys: string[],
  callback: () => void,
  disabled = false
) {
  useEffect(() => {
    if (disabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const pressedKeys = new Set<string>();

      if (e.ctrlKey || e.metaKey) pressedKeys.add("ctrl");
      if (e.altKey) pressedKeys.add("alt");
      if (e.shiftKey) pressedKeys.add("shift");
      
      // Use lowercase key name
      pressedKeys.add(e.key.toLowerCase());

      const isMatch = keys.every(k => pressedKeys.has(k.toLowerCase()));
      // Ensure no extra modifiers are pressed (e.g. if looking for ctrl+s, alt should not be pressed)
      const matchesExactly = isMatch && pressedKeys.size === keys.length;

      if (matchesExactly) {
        e.preventDefault();
        callback();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [keys, callback, disabled]);
}
